import React, { useState, useEffect } from 'react';
import { getImageUrl } from '../utils/imageUrl';
import config from '../config';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { Users, Car, MessageCircle, Heart, Trash2, Eye, Settings, Shield, Phone, TrendingUp, Store, UserPlus } from 'lucide-react';
import API from '../services/api';

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [onglet, setOnglet] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [annonces, setAnnonces] = useState([]);
  const [configuration, setConfiguration] = useState([]);
  const [configModifiee, setConfigModifiee] = useState({});
  const [publicites, setPublicites] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [nouvellePub, setNouvellePub] = useState({
    titre: '', lien: '', emplacement: 'homepage_top', annonceur: '', date_fin: ''
  });
  const [imagePub, setImagePub] = useState(null);
  const [loadingPub, setLoadingPub] = useState(false);
  const pubImageRef = React.useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    chargerDashboard();
  }, [user]);

  const chargerDashboard = async () => {
    try {
      const res = await API.get('/admin/dashboard');
      setStats(res.data.stats);
    } finally {
      setLoading(false);
    }
  };

  const chargerUsers = async () => {
    const res = await API.get('/admin/users');
    setUsers(res.data.users);
  };

  const chargerAnnonces = async () => {
    const res = await API.get('/admin/annonces');
    setAnnonces(res.data.annonces);
  };

  const chargerConfiguration = async () => {
    const res = await API.get('/admin/configuration');
    setConfiguration(res.data.configuration);
  };

  const chargerPublicites = async () => {
    const res = await API.get('/publicites');
    setPublicites(res.data.publicites);
  };

  const chargerVerifications = async () => {
    const res = await API.get('/verifications/admin/demandes');
    setVerifications(res.data.demandes);
  };

  const handleOnglet = (o) => {
    setOnglet(o);
    if (o === 'users') chargerUsers();
    if (o === 'annonces') chargerAnnonces();
    if (o === 'configuration') chargerConfiguration();
    if (o === 'publicites') chargerPublicites();
    if (o === 'verifications') chargerVerifications();
  };

  const modifierRole = async (id, role) => {
    await API.put(`/admin/users/${id}/role`, { role });
    chargerUsers();
  };

  const supprimerUser = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    await API.delete(`/admin/users/${id}`);
    chargerUsers();
  };

  const modifierStatut = async (id, statut) => {
    await API.put(`/admin/annonces/${id}/statut`, { statut });
    chargerAnnonces();
  };

  const supprimerAnnonce = async (id) => {
    if (!window.confirm('Supprimer cette annonce ?')) return;
    await API.delete(`/admin/annonces/${id}`);
    chargerAnnonces();
  };

  const sauvegarderConfig = async (cle) => {
    try {
      await API.put(`/admin/configuration/${cle}`, { valeur: configModifiee[cle] });
      alert('Configuration mise à jour !');
      chargerConfiguration();
    } catch (err) {
      alert('Erreur lors de la mise à jour');
    }
  };

  const uploaderHeroImage = async (cle, file) => {
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('image', file);
      await API.post(`/hero/upload/${cle}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert('Image uploadée !');
      chargerConfiguration();
    } catch (err) {
      alert('Erreur lors de l\'upload : ' + (err.response?.data?.message || err.message));
    }
  };

  const creerPublicite = async (e) => {
    e.preventDefault();
    if (!imagePub) { alert('Veuillez sélectionner une image'); return; }
    setLoadingPub(true);
    try {
      const formData = new FormData();
      formData.append('image', imagePub);
      formData.append('titre', nouvellePub.titre);
      formData.append('lien', nouvellePub.lien);
      formData.append('emplacement', nouvellePub.emplacement);
      formData.append('annonceur', nouvellePub.annonceur);
      formData.append('date_fin', nouvellePub.date_fin);
      const response = await fetch(`${config.API_URL}/api/publicites`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData
      });
      const data = await response.json();
      if (!response.ok) {
        alert('Erreur : ' + (data.message || 'Création échouée'));
        return;
      }
      alert('Publicité créée !');
      setNouvellePub({ titre: '', lien: '', emplacement: 'homepage_top', annonceur: '', date_fin: '' });
      setImagePub(null);
      chargerPublicites();
    } catch (err) {
      alert('Erreur lors de la création');
    } finally {
      setLoadingPub(false);
    }
  };

  const togglePublicite = async (id) => {
    await API.put(`/publicites/${id}/toggle`);
    chargerPublicites();
  };

  const supprimerPublicite = async (id) => {
    if (!window.confirm('Supprimer cette publicité ?')) return;
    await API.delete(`/publicites/${id}`);
    chargerPublicites();
  };

  const traiterVerification = async (id, statut) => {
    await API.put(`/verifications/admin/traiter/${id}`, { statut });
    chargerVerifications();
  };

  const attribuerBadge = async (userId, niveau) => {
    await API.put(`/verifications/admin/badge/${userId}`, { niveau });
    chargerVerifications();
  };

  const onglets = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'users', label: 'Utilisateurs' },
    { id: 'annonces', label: 'Annonces' },
    { id: 'verifications', label: '🛡 Vérifications' },
    { id: 'publicites', label: 'Publicités' },
    { id: 'configuration', label: 'Configuration' }
  ];

  if (loading) return (
    <div className="min-h-screen bg-gray-50"><Navbar />
      <div className="text-center py-20 text-gray-400">Chargement...</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div style={{ background: '#0A0A0A' }} className="py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-white text-2xl font-black">Panel Administrateur</h1>
          <p style={{ color: '#F5A623' }} className="text-sm mt-1">Gestion de la plateforme JJ Cars</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">

        {/* Onglets */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {onglets.map(o => (
            <button
              key={o.id}
              onClick={() => handleOnglet(o.id)}
              style={{
                background: onglet === o.id ? '#F5A623' : 'white',
                color: onglet === o.id ? '#000' : '#6b7280',
                border: onglet === o.id ? 'none' : '1px solid #e5e7eb'
              }}
              className="px-4 py-2 rounded-xl font-bold text-sm whitespace-nowrap hover:opacity-90 transition"
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* Dashboard */}
        {onglet === 'dashboard' && stats && (
          <div className="space-y-6">
            {/* North Star — déclencheur phase 2 : 250 offres actives + contacts WhatsApp stables */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">North Star — déclencheur phase 2</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Offre active', value: stats.offre_active, sub: `${stats.annonces_actives} ventes + ${stats.locations_actives} locations · objectif 250`, icon: Car, color: '#F5A623' },
                  { label: 'Contacts WhatsApp', value: stats.contacts_whatsapp, sub: `${stats.contacts_whatsapp_annonces} ventes + ${stats.contacts_whatsapp_locations} locations`, icon: Phone, color: '#25D366' },
                  { label: 'Vendeurs actifs', value: stats.vendeurs_actifs, sub: 'uniques avec offre active', icon: Store, color: '#8b5cf6' },
                  { label: 'Taux de contact', value: `${stats.taux_contact}%`, sub: 'contacts / vues annonces', icon: TrendingUp, color: '#3b82f6' }
                ].map(({ label, value, sub, icon: Icon, color }) => (
                  <div key={label} className="bg-white rounded-2xl shadow-sm p-6 border border-gray-100">
                    <div className="flex items-center justify-between mb-3">
                      <div style={{ background: color + '15' }} className="w-10 h-10 rounded-xl flex items-center justify-center">
                        <Icon size={20} style={{ color }} />
                      </div>
                    </div>
                    <p className="text-3xl font-black text-gray-900">{value}</p>
                    <p className="text-gray-700 text-sm mt-1 font-semibold">{label}</p>
                    <p className="text-gray-400 text-xs mt-0.5">{sub}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Activité 7 derniers jours */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">7 derniers jours</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Nouveaux utilisateurs', value: stats.nouveaux_users_7j, icon: UserPlus, color: '#3b82f6' },
                  { label: 'Nouvelles annonces', value: stats.nouvelles_annonces_7j, icon: Car, color: '#F5A623' }
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="bg-white rounded-2xl shadow-sm p-6 text-center border border-gray-100">
                    <div style={{ background: color + '15' }} className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Icon size={22} style={{ color }} />
                    </div>
                    <p className="text-3xl font-black text-gray-900">{value}</p>
                    <p className="text-gray-400 text-sm mt-1">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Compteurs globaux historiques */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-3">Totaux cumulés</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Utilisateurs', value: stats.users, icon: Users, color: '#3b82f6' },
                  { label: 'Annonces', value: stats.annonces, icon: Car, color: '#F5A623' },
                  { label: 'Locations', value: stats.locations, icon: Car, color: '#10b981' },
                  { label: 'Vues annonces', value: stats.total_vues, icon: Eye, color: '#0ea5e9' },
                  { label: 'Messages', value: stats.messages, icon: MessageCircle, color: '#8b5cf6' },
                  { label: 'Favoris', value: stats.favoris, icon: Heart, color: '#ef4444' }
                ].map(({ label, value, icon: Icon, color }) => (
                  <div key={label} className="bg-white rounded-2xl shadow-sm p-6 text-center border border-gray-100">
                    <div style={{ background: color + '15' }} className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3">
                      <Icon size={22} style={{ color }} />
                    </div>
                    <p className="text-3xl font-black text-gray-900">{value}</p>
                    <p className="text-gray-400 text-sm mt-1">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Utilisateurs */}
        {onglet === 'users' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
            <div style={{ background: '#0A0A0A' }} className="p-4">
              <p className="text-white font-bold">Gestion des utilisateurs ({users.length})</p>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Utilisateur</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Email</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Rôle</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Badge</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-t border-gray-50 hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div style={{ background: '#F5A623' }} className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-black font-black text-xs">{u.prenom?.[0]}{u.nom?.[0]}</span>
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900 text-sm">{u.prenom} {u.nom}</p>
                          <p className="text-xs text-gray-400">{u.telephone}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{u.email}</td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => modifierRole(u.id, e.target.value)}
                        className="border border-gray-200 rounded-xl px-2 py-1 text-sm focus:outline-none focus:border-yellow-400"
                      >
                        <option value="acheteur">Acheteur</option>
                        <option value="vendeur_particulier">Vendeur particulier</option>
                        <option value="vendeur_pro">Vendeur pro</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {u.niveau_verification > 0 ? (
                        <span style={{
                          background: u.niveau_verification === 1 ? '#3b82f6' : u.niveau_verification === 2 ? '#8b5cf6' : '#F5A623',
                          color: 'white'
                        }} className="px-2 py-0.5 rounded-full text-xs font-bold">
                          N{u.niveau_verification} ✓
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Non vérifié</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {u.id !== user.id && (
                        <button onClick={() => supprimerUser(u.id)}
                          className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition">
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Annonces */}
        {onglet === 'annonces' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
            <div style={{ background: '#0A0A0A' }} className="p-4">
              <p className="text-white font-bold">Gestion des annonces ({annonces.length})</p>
            </div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Annonce</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Vendeur</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Prix</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Statut</th>
                  <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {annonces.map(a => (
                  <tr key={a.id} className="border-t border-gray-50 hover:bg-gray-50 transition">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900 text-sm">{a.titre}</p>
                      <p className="text-xs text-gray-400">{a.vues} vues</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{a.prenom} {a.nom}</td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">
                      {parseInt(a.prix).toLocaleString('fr-FR')} FCFA
                    </td>
                    <td className="px-4 py-3">
                      <select value={a.statut} onChange={(e) => modifierStatut(a.id, e.target.value)}
                        className="border border-gray-200 rounded-xl px-2 py-1 text-sm focus:outline-none focus:border-yellow-400">
                        <option value="active">Active</option>
                        <option value="vendue">Vendue</option>
                        <option value="expiree">Expirée</option>
                        <option value="suspendue">Suspendue</option>
                      </select>
                    </td>
                    <td className="px-4 py-3 flex gap-2">
                      <button onClick={() => navigate(`/annonces/${a.id}`)}
                        className="p-2 text-blue-400 hover:bg-blue-50 rounded-xl transition">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => supprimerAnnonce(a.id)}
                        className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Vérifications */}
        {onglet === 'verifications' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
            <div style={{ background: '#0A0A0A' }} className="p-4 flex items-center gap-2">
              <Shield size={18} style={{ color: '#F5A623' }} />
              <p className="text-white font-bold">Demandes de vérification ({verifications.length})</p>
            </div>
            {verifications.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Shield size={40} className="mx-auto mb-3 text-gray-200" />
                <p>Aucune demande de vérification</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Vendeur</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Niveau demandé</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Badge actuel</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Document</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Statut</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {verifications.map(v => (
                    <tr key={v.id} className="border-t border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-4 py-3">
                        <p className="font-semibold text-sm text-gray-900">{v.prenom} {v.nom}</p>
                        <p className="text-xs text-gray-400">{v.email}</p>
                        <p className="text-xs text-gray-400">{v.telephone}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded-full text-xs font-bold text-white"
                          style={{ background: v.niveau === 1 ? '#3b82f6' : v.niveau === 2 ? '#8b5cf6' : '#F5A623' }}>
                          Niveau {v.niveau}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {v.niveau_verification > 0 ? (
                          <span style={{
                            background: v.niveau_verification === 1 ? '#3b82f6' : v.niveau_verification === 2 ? '#8b5cf6' : '#F5A623',
                            color: 'white'
                          }} className="px-2 py-0.5 rounded-full text-xs font-bold">
                            N{v.niveau_verification} ✓
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Aucun</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {v.document_url ? (
                          <a href={getImageUrl(v.document_url)} target="_blank"
                            rel="noopener noreferrer" style={{ color: '#F5A623' }}
                            className="text-xs font-semibold underline">
                            Voir document
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400">Aucun document</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          v.statut === 'en_attente' ? 'bg-yellow-100 text-yellow-600' :
                          v.statut === 'valide' ? 'bg-green-100 text-green-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {v.statut === 'en_attente' ? '⏳ En attente' :
                           v.statut === 'valide' ? '✅ Validé' : '❌ Refusé'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {v.statut === 'en_attente' && (
                          <div className="flex gap-2">
                            <button onClick={() => traiterVerification(v.id, 'valide')}
                              className="px-3 py-1 bg-green-100 text-green-600 rounded-xl text-xs font-bold hover:bg-green-200 transition">
                              ✅ Valider
                            </button>
                            <button onClick={() => traiterVerification(v.id, 'refuse')}
                              className="px-3 py-1 bg-red-100 text-red-600 rounded-xl text-xs font-bold hover:bg-red-200 transition">
                              ❌ Refuser
                            </button>
                          </div>
                        )}
                        <div className="flex gap-1 mt-1">
                          <p className="text-xs text-gray-400 mr-1">Badge:</p>
                          {[1, 2, 3].map(n => (
                            <button key={n} onClick={() => attribuerBadge(v.utilisateur_id, n)}
                              style={{ background: n === 1 ? '#3b82f6' : n === 2 ? '#8b5cf6' : '#F5A623' }}
                              className="px-2 py-0.5 text-white rounded-lg text-xs font-bold hover:opacity-80 transition">
                              N{n}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Publicités */}
        {onglet === 'publicites' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div style={{ background: '#0A0A0A' }} className="p-4">
                <p className="text-white font-bold">Ajouter une publicité</p>
              </div>
              <form onSubmit={creerPublicite} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Titre</label>
                    <input type="text" required value={nouvellePub.titre}
                      onChange={(e) => setNouvellePub({ ...nouvellePub, titre: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                      placeholder="Titre de la publicité" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Annonceur</label>
                    <input type="text" value={nouvellePub.annonceur}
                      onChange={(e) => setNouvellePub({ ...nouvellePub, annonceur: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                      placeholder="Nom de l'annonceur" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Emplacement</label>
                    <select value={nouvellePub.emplacement}
                      onChange={(e) => setNouvellePub({ ...nouvellePub, emplacement: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400">
                      <option value="homepage_top">Accueil — Bannière principale (1200x150px)</option>
                      <option value="accueil_bottom">Accueil — Bas de page (1200x120px)</option>
                      <option value="recherche_top">Annonces — Sous les filtres (1200x120px)</option>
                      <option value="locations_top">Locations — Sous le header (1200x120px)</option>
                      <option value="detail_annonce_bottom">Détail annonce — Bas de page (1200x120px)</option>
                      <option value="homepage_sidebar">Sidebar accueil — 300x250px</option>
                      <option value="annonce_sidebar">Sidebar annonce — 300x250px</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">Date de fin</label>
                    <input type="date" value={nouvellePub.date_fin}
                      onChange={(e) => setNouvellePub({ ...nouvellePub, date_fin: e.target.value })}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Lien (optionnel)</label>
                  <input type="url" value={nouvellePub.lien}
                    onChange={(e) => setNouvellePub({ ...nouvellePub, lien: e.target.value })}
                    className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-yellow-400"
                    placeholder="https://..." />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 mb-1">Image de la bannière</label>
                  <div onClick={() => pubImageRef.current.click()}
                    style={{ border: '2px dashed #e5e7eb' }}
                    className="rounded-xl p-4 text-center cursor-pointer hover:border-yellow-400 transition">
                    {imagePub ? (
                      <div className="flex items-center justify-center gap-2">
                        <img src={URL.createObjectURL(imagePub)} alt="apercu" className="h-16 object-contain rounded-lg" />
                        <p className="text-sm text-gray-500">{imagePub.name}</p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400">Cliquez pour sélectionner une image</p>
                    )}
                  </div>
                  <input ref={pubImageRef} type="file" accept="image/*"
                    onChange={(e) => setImagePub(e.target.files[0])} className="hidden" />
                </div>
                <button type="submit" disabled={loadingPub} style={{ background: '#F5A623' }}
                  className="w-full text-black py-2 rounded-xl font-bold hover:opacity-90 transition disabled:opacity-50">
                  {loadingPub ? 'Création...' : 'Créer la publicité'}
                </button>
              </form>
            </div>

            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div style={{ background: '#0A0A0A' }} className="p-4">
                <p className="text-white font-bold">Publicités ({publicites.length})</p>
              </div>
              {publicites.length === 0 ? (
                <div className="text-center py-12 text-gray-400"><p>Aucune publicité pour le moment</p></div>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Bannière</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Titre</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Emplacement</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Annonceur</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Statut</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publicites.map(pub => (
                      <tr key={pub.id} className="border-t border-gray-50 hover:bg-gray-50 transition">
                        <td className="px-4 py-3">
                          <img src={getImageUrl(pub.image_url)} alt={pub.titre}
                            className="h-12 w-20 object-cover rounded-lg" />
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900">{pub.titre}</td>
                        <td className="px-4 py-3 text-xs text-gray-500">{pub.emplacement}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{pub.annonceur}</td>
                        <td className="px-4 py-3">
                          <button onClick={() => togglePublicite(pub.id)}
                            className={`px-3 py-1 rounded-full text-xs font-bold ${pub.actif ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`}>
                            {pub.actif ? '✅ Active' : '⏸ Inactive'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <button onClick={() => supprimerPublicite(pub.id)}
                            className="p-2 text-red-400 hover:bg-red-50 rounded-xl transition">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {/* Configuration */}
        {onglet === 'configuration' && (
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
            <div style={{ background: '#0A0A0A' }} className="p-4 flex items-center gap-2">
              <Settings size={18} style={{ color: '#F5A623' }} />
              <p className="text-white font-bold">Configuration de la plateforme</p>
            </div>
            <div className="p-4">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Paramètre</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Description</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Valeur</th>
                    <th className="text-left px-4 py-3 text-sm font-semibold text-gray-500">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {configuration.map(config => (
                    <tr key={config.cle} className="border-t border-gray-50 hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-xs font-mono text-gray-500">{config.cle}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{config.description}</td>
                      <td className="px-4 py-3">
                        <input type="text" defaultValue={config.valeur}
                          onChange={(e) => setConfigModifiee({ ...configModifiee, [config.cle]: e.target.value })}
                          className="border border-gray-200 rounded-xl px-3 py-1 text-sm w-full min-w-[8rem] focus:outline-none focus:border-yellow-400 transition" />
                        {(config.cle.startsWith('hero_image_') || config.cle === 'login_image' || config.cle === 'register_image') && (
                          <div className="mt-2 flex items-center gap-3">
                            {config.valeur && (
                              <img src={config.valeur} alt="" className="h-16 rounded-lg object-cover border border-gray-200" />
                            )}
                            <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-2 rounded-lg transition">
                              Uploader une image
                              <input type="file" accept="image/*" className="hidden"
                                onChange={(e) => uploaderHeroImage(config.cle, e.target.files[0])} />
                            </label>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => sauvegarderConfig(config.cle)}
                          style={{ background: '#F5A623' }}
                          className="text-black px-3 py-1 rounded-xl text-sm font-bold hover:opacity-90 transition">
                          Sauvegarder
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;